/**
 * Coordinate Formatting Utilities
 * Shared module for coordinate display and conversion
 */

export interface FormattedCoordinates {
  dd: string;   // Decimal Degrees: 60.123456°, -111.654321°
  dmm: string;  // Degrees Decimal Minutes: 60° 7.408' N, 111° 39.259' W
  dms: string;  // Degrees Minutes Seconds: 60° 7' 24.6" N, 111° 39' 15.5" W
}

/**
 * Convert decimal coordinate to Degrees Decimal Minutes (DMM)
 * NWT default format
 */
export function toDMM(coord: number, isLat: boolean): string {
  const absolute = Math.abs(coord);
  const degrees = Math.floor(absolute);
  const minutes = ((absolute - degrees) * 60).toFixed(3);
  const direction = isLat
    ? (coord >= 0 ? 'N' : 'S')
    : (coord >= 0 ? 'E' : 'W');
  return `${degrees}° ${minutes}' ${direction}`;
}

/**
 * Convert decimal coordinate to Degrees Minutes Seconds (DMS)
 */
export function toDMS(coord: number, isLat: boolean): string {
  const absolute = Math.abs(coord);
  const degrees = Math.floor(absolute);
  const minutesFloat = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesFloat);
  const seconds = ((minutesFloat - minutes) * 60).toFixed(1);
  const direction = isLat
    ? (coord >= 0 ? 'N' : 'S')
    : (coord >= 0 ? 'E' : 'W');
  return `${degrees}° ${minutes}' ${seconds}" ${direction}`;
}

/**
 * Format coordinates in all three standard formats
 */
export function formatCoordinates(lat: number, lon: number): FormattedCoordinates {
  // Decimal degrees
  const dd = `${lat.toFixed(6)}°, ${lon.toFixed(6)}°`;

  // DMM (Degrees Decimal Minutes) - NWT default
  const dmm = `${toDMM(lat, true)}, ${toDMM(lon, false)}`;

  // DMS (Degrees Minutes Seconds)
  const dms = `${toDMS(lat, true)}, ${toDMS(lon, false)}`;

  return { dd, dmm, dms };
}

/**
 * Copy text to clipboard with fallback for older browsers
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    return true;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
}
